import { useCallback, useState } from 'react';

// ============================================================================
// Tipos e defaults
// ============================================================================

export type CriterioModo = 'mensal' | 'trimestral' | 'exercicio' | 'avancado';

export interface ICriterioFechamento {
  modo: CriterioModo;
  diasLr?: number;
  diasLp?: number;
  diasSn?: number;
  diasMei?: number;
  diasOutros?: number;
}

export const CRITERIO_DEFAULT: ICriterioFechamento = {
  modo: 'mensal',
};

// Mesmos valores do backend (DEFAULTS_AVANCADO_DIAS).
// Calibrados pela distribuicao real da carteira (P50/P75 de dias_atraso por regime).
export const CRITERIO_DEFAULTS_AVANCADO = {
  diasLr: 90,
  diasLp: 120,
  diasSn: 180,
  diasMei: 365,
  diasOutros: 180,
};

const STORAGE_KEY = 'fechamentos:criterio';

// ============================================================================
// Helpers para enviar como querystring no axios
// ============================================================================

export const criterioToParams = (c: ICriterioFechamento): Record<string, string | number> => {
  const out: Record<string, string | number> = { criterio: c.modo };
  if (c.modo === 'avancado') {
    out.dias_lr = c.diasLr ?? CRITERIO_DEFAULTS_AVANCADO.diasLr;
    out.dias_lp = c.diasLp ?? CRITERIO_DEFAULTS_AVANCADO.diasLp;
    out.dias_sn = c.diasSn ?? CRITERIO_DEFAULTS_AVANCADO.diasSn;
    out.dias_mei = c.diasMei ?? CRITERIO_DEFAULTS_AVANCADO.diasMei;
    out.dias_outros = c.diasOutros ?? CRITERIO_DEFAULTS_AVANCADO.diasOutros;
  }
  return out;
};

// ============================================================================
// Descricao humana (titulo + janela calculada + detalhe)
// ============================================================================

const formatDate = (d: Date): string =>
  d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

const formatMesAno = (d: Date): string =>
  d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });

const primeiroDiaNMesesAtras = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth() - n, 1);

const ultimoDiaMesPassado = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 0);

const primeiroDiaTrimestreAnterior = (d: Date): Date => {
  const trimInicioMes = Math.floor(d.getMonth() / 3) * 3;
  if (trimInicioMes === 0) return new Date(d.getFullYear() - 1, 9, 1);
  return new Date(d.getFullYear(), trimInicioMes - 3, 1);
};

export interface ICriterioDescricao {
  titulo: string;
  subtitulo: string;
  janela: string;
  detalhe: string;
  badge: string;
}

export const describeCriterio = (c: ICriterioFechamento): ICriterioDescricao => {
  const hoje = new Date();
  const mesPassado = formatMesAno(primeiroDiaNMesesAtras(hoje, 1));
  const mesAtual = formatMesAno(hoje);

  if (c.modo === 'mensal') {
    // M-2: ignora mes atual (em curso) e mes anterior (em fechamento).
    return {
      titulo: 'Mensal',
      subtitulo: 'última competência fechada',
      janela: `fechamento ≥ ${formatDate(primeiroDiaNMesesAtras(hoje, 2))}`,
      detalhe: `Como uma fatura: ${mesAtual} ainda está em curso e ${mesPassado} está sendo fechado pelo escritório — nenhum dos dois é cobrado. A empresa precisa ter fechado a competência anterior a ${mesPassado}.`,
      badge: 'Estrito',
    };
  }
  if (c.modo === 'trimestral') {
    return {
      titulo: 'Trimestral',
      subtitulo: 'trimestre passado completo',
      janela: `fechamento ≥ ${formatDate(primeiroDiaTrimestreAnterior(hoje))}`,
      detalhe:
        'Em dia = fechou pelo menos o trimestre passado completo. O trimestre atual é ignorado por estar em curso.',
      badge: 'Intermediário',
    };
  }
  if (c.modo === 'exercicio') {
    return {
      titulo: 'Por exercício (ano fiscal)',
      subtitulo: 'desde 1º jan',
      janela: `fechamento ≥ ${formatDate(new Date(hoje.getFullYear(), 0, 1))}`,
      detalhe:
        'Em dia = fechou pelo menos o exercício passado. Bate com a realidade contábil brasileira (a maioria fecha em dezembro).',
      badge: 'Tolerante',
    };
  }
  // avancado: dias contados a partir do fim do mes passado (ultima
  // competencia "vencida") — mantem a logica de fatura.
  const referencia = ultimoDiaMesPassado(hoje);
  const dias = {
    LR: c.diasLr ?? CRITERIO_DEFAULTS_AVANCADO.diasLr,
    LP: c.diasLp ?? CRITERIO_DEFAULTS_AVANCADO.diasLp,
    SN: c.diasSn ?? CRITERIO_DEFAULTS_AVANCADO.diasSn,
    MEI: c.diasMei ?? CRITERIO_DEFAULTS_AVANCADO.diasMei,
    Outros: c.diasOutros ?? CRITERIO_DEFAULTS_AVANCADO.diasOutros,
  };
  const detalhe = `Janela em dias por regime, contada a partir do fim de ${mesPassado} (${formatDate(referencia)}). ${Object.entries(
    dias,
  )
    .map(([k, v]) => `${k} ${v}d`)
    .join(' · ')}`;
  return {
    titulo: 'Avançado',
    subtitulo: 'por regime',
    janela: `referência: ${formatDate(referencia)} − dias por regime`,
    detalhe,
    badge: 'Personalizado',
  };
};

// ============================================================================
// Hook (state + persistencia em localStorage)
// ============================================================================

const MODOS_VALIDOS: CriterioModo[] = ['mensal', 'trimestral', 'exercicio', 'avancado'];

const loadFromStorage = (): ICriterioFechamento => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return CRITERIO_DEFAULT;
    const parsed = JSON.parse(raw);
    // Valida `modo` contra a lista — sem isso, um localStorage corrompido
    // (ex: { modo: "xpto" }) seria enviado ao backend e geraria 422.
    if (!parsed?.modo || !MODOS_VALIDOS.includes(parsed.modo)) {
      return CRITERIO_DEFAULT;
    }
    return parsed as ICriterioFechamento;
  } catch {
    return CRITERIO_DEFAULT;
  }
};

export const useCriterioFechamento = () => {
  const [criterio, setCriterioState] = useState<ICriterioFechamento>(loadFromStorage);

  const setCriterio = useCallback((next: ICriterioFechamento) => {
    setCriterioState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage indisponivel/cheio — segue usando o estado em memoria
    }
  }, []);

  const reset = useCallback(() => {
    setCriterio(CRITERIO_DEFAULT);
  }, [setCriterio]);

  return { criterio, setCriterio, reset };
};
